/**
 * DossierSignatureBadge — Header chip indicating whether the
 * embarque's dossier has been cryptographically sealed (DIAN-03).
 */
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { useLatestDossierSignature } from "@/hooks/useLatestDossierSignature";

interface Props {
  embarqueId: string;
}

export function DossierSignatureBadge({ embarqueId }: Props) {
  const { data, isLoading } = useLatestDossierSignature(embarqueId);

  if (isLoading) return null;

  const sealed = !!data;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={
              sealed
                ? "gap-1 cursor-help border-emerald-600/40 text-emerald-700 dark:text-emerald-400"
                : "gap-1 cursor-help border-muted-foreground/30 text-muted-foreground"
            }
          >
            {sealed ? (
              <ShieldCheck className="h-3 w-3 text-emerald-600" />
            ) : (
              <ShieldAlert className="h-3 w-3" />
            )}
            {sealed ? "Dossier Sellado" : "Firma Pendiente"}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs max-w-[260px]">
          Firma criptográfica HMAC-SHA256
          {sealed && data?.signed_at && (
            <div className="text-[10px] text-muted-foreground mt-1 tabular-nums">
              {new Date(data.signed_at).toLocaleString("es-CO")}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
